import { Injectable, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { EnterpriseFeatureService } from 'src/app/services/enterprise-features.service';
import { PermissionService } from 'src/app/security/permission.service';
import { AppMenuPermissions, resolveAppMenuPermissions } from 'src/app/security/app-menu-permissions';
import { LocaleService, SupportedLocale } from 'src/app/i18n/locale.service';

export interface AppMenuBuildResult {
    profileMenuItems: MenuItem[];
    toolbarMenuItems: MenuItem[];
}

@Injectable({ providedIn: 'root' })
export class AppMenuBuilderService {
    private readonly enterpriseFeatureService = inject(EnterpriseFeatureService);
    private readonly permissionService = inject(PermissionService);
    private readonly localeService = inject(LocaleService);

    buildMenus(onLogout: () => void): AppMenuBuildResult {
        const isEnterprise = this.enterpriseFeatureService.getEnableEnterpriseAccounts();
        const isTrialReadOnly = this.enterpriseFeatureService.isReadOnlyGrace();
        const isCrmSubscribed = this.enterpriseFeatureService.getIsCrmSubscribed();
        const isAiChatSubscribed = this.enterpriseFeatureService.getIsAiChatSubscribed();
        const isWorkflowAutomationSubscribed = this.enterpriseFeatureService.getIsWorkflowAutomationSubscribed();
        const isWasenderSubscribed = this.enterpriseFeatureService.getIsWasenderSubscribed();
        const isFuelManagementSubscribed = this.enterpriseFeatureService.getIsFuelManagementSubscribed();
        const isContainerTrackingSubscribed = this.enterpriseFeatureService.getIsContainerTrackingTrackingSubscribed();
        const isCustomsClearanceSubscribed = this.enterpriseFeatureService.getIsCustomsClearanceSubscribed();
        const isPosmSubscribed = this.enterpriseFeatureService.getIsPosmSubscribed();
        const isWarehousingSubscribed = this.enterpriseFeatureService.getIsWarehousingSubscribed();
        const isFleetSubscribed = this.enterpriseFeatureService.getIsFleetSubscribed();
        const isGpsTrackingSubscribed = this.enterpriseFeatureService.getIsGPSTrackingSubscribed();
        const isPartnerManagementSubscribed = this.enterpriseFeatureService.getIsPartnerManagementSubscribed();
        const isContractsSubscribed = this.enterpriseFeatureService.getIsContractsSubscribed();
        const isHrSubscribed = this.enterpriseFeatureService.getIsHrSubscribed();
        const isAuditSubscribed = this.enterpriseFeatureService.getIsAuditSubscribed();
        const isApiPlatformSubscribed = this.enterpriseFeatureService.getIsApiPlatformSubscribed();
        const isProjectsSubscribed = this.enterpriseFeatureService.getIsProjectsSubscribed();
        const can = resolveAppMenuPermissions(this.permissionService);

        return {
            profileMenuItems: this.buildProfileMenu(can, onLogout),
            toolbarMenuItems: this.localizeNavigation([
                {
                    label: 'Dashboard',
                    icon: 'fa-solid fa-chart-pie',
                    routerLink: ['/dashboard'],
                    visible: true
                },
                {
                    label: 'Workspace',
                    icon: 'fa-solid fa-table-columns',
                    visible: can.canViewWorkspace,
                    items: [
                        {
                            label: 'Petty Cash',
                            icon: 'pi pi-wallet',
                            routerLink: ['/workspace/petty-cash'],
                            visible: can.canViewPettyCash,
                        },
                        {
                            label: 'My HR',
                            icon: 'fa-solid fa-id-card',
                            routerLink: ['/my-hr'],
                            visible: can.canViewHrSelf || can.canViewHrManager,
                        },
                        {
                            label: 'AI Chat',
                            icon: 'fa-solid fa-robot',
                            routerLink: ['/ai-chat'],
                            visible: isAiChatSubscribed && can.canViewAiChat,
                        },
                        {
                            label: 'WhatsApp Chat',
                            icon: 'pi pi-whatsapp',
                            routerLink: ['/wasender/chat'],
                            visible: isWasenderSubscribed && can.canViewWasenderChat,
                        },
                        {
                            label: 'Notifications',
                            icon: 'pi pi-bell',
                            routerLink: ['/productivity/notifications'],
                            visible: can.canViewNotifications
                        },
                        {
                            label: 'Tasks',
                            icon: 'pi pi-check-square',
                            routerLink: ['/productivity/tasks'],
                            visible: can.canViewTasks
                        },
                        {
                            label: 'Incidents',
                            icon: 'pi pi-exclamation-triangle',
                            routerLink: ['/productivity/incidents'],
                            visible: can.canViewIncidents
                        },
                        {
                            label: 'Report Builder',
                            icon: 'fa-solid fa-chart-column',
                            routerLink: ['/reports'],
                            visible: isEnterprise && can.canViewReportBuilder
                        },
                        {
                            label: 'Help Center',
                            icon: 'fa-solid fa-book-open',
                            routerLink: ['/help'],
                            visible: true,
                        }
                    ]
                },
                {
                    label: 'Commercial',
                    icon: 'fa-solid fa-handshake',
                    visible: (isCrmSubscribed && can.canViewCrm)
                        || (isPartnerManagementSubscribed && can.canViewPartners)
                        || (isContractsSubscribed && can.canViewContracts),
                    items: [
                        {
                            label: 'CRM',
                            icon: 'fa-solid fa-address-book',
                            visible: isCrmSubscribed && (can.canViewCrm || can.canManageCustomerPortalAccess || (isContractsSubscribed && can.canViewContracts)),
                            items: [
                                {
                                    label: 'Dashboard',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/crm/dashboard'],
                                    visible: can.canViewCrmDashboard
                                },
                                {
                                    label: 'Leads',
                                    icon: 'fa-solid fa-user-plus',
                                    routerLink: ['/crm/leads'],
                                    visible: can.canViewCrmLeads
                                },
                                {
                                    label: 'Prospects',
                                    icon: 'fa-solid fa-user-check',
                                    routerLink: ['/crm/prospects'],
                                    visible: can.canViewCrmProspects
                                },
                                {
                                    label: 'Clients',
                                    icon: 'fa-solid fa-building-user',
                                    routerLink: ['/crm/clients'],
                                    visible: can.canViewCrmClients
                                },
                                {
                                    label: 'Client Accounts',
                                    icon: 'fa-solid fa-sitemap',
                                    routerLink: ['/crm/accounts'],
                                    visible: can.canViewCrmAccounts
                                },
                                {
                                    label: 'Customer Portal Access',
                                    icon: 'fa-solid fa-user-shield',
                                    routerLink: ['/crm/customer-portal-access'],
                                    visible: can.canManageCustomerPortalAccess
                                },
                                {
                                    label: 'Customer Support Queue',
                                    icon: 'fa-solid fa-headset',
                                    routerLink: ['/crm/customer-support'],
                                    visible: can.canManageCustomerPortalAccess
                                },
                                {
                                    label: 'Merchandisers',
                                    icon: 'fa-solid fa-user-tag',
                                    routerLink: ['/crm/merchandisers'],
                                    visible: can.canViewCrmMerchandisers
                                },
                                {
                                    label: 'KAM Assignments',
                                    icon: 'fa-solid fa-id-card-clip',
                                    routerLink: ['/crm/assignments'],
                                    visible: can.canViewCrmAssignments
                                },
                                {
                                    label: 'Quotations',
                                    icon: 'fa-solid fa-file-invoice-dollar',
                                    routerLink: ['/crm/quotations'],
                                    visible: can.canViewCrmQuotations
                                },
                                {
                                    label: 'Commercial Agreements',
                                    icon: 'fa-solid fa-file-contract',
                                    routerLink: ['/contracts'],
                                    visible: isContractsSubscribed && can.canViewContracts
                                }
                            ]
                        },
                        {
                            label: 'Partners',
                            icon: 'pi pi-fw pi-briefcase',
                            visible: isPartnerManagementSubscribed && can.canViewPartners,
                            items: [
                                {
                                    label: 'Clients',
                                    icon: 'pi pi-fw pi-building',
                                    routerLink: ['party/clients'],
                                    visible: can.canViewPartyClients
                                },
                                {
                                    label: 'Contacts',
                                    icon: 'pi pi-fw pi-users',
                                    routerLink: ['party/contacts'],
                                    visible: can.canViewPartyContacts
                                }
                            ]
                        },
                    ]
                },
                {
                    label: 'Projects & Logistics',
                    icon: 'fa-solid fa-layer-group',
                    visible: (isProjectsSubscribed && can.canViewLogisticsProjects)
                        || (isPosmSubscribed && can.canViewPosmProjects)
                        || (isWarehousingSubscribed && can.canViewWarehousing)
                        || can.canViewFieldWork
                        || can.canViewFieldWorkEquipment
                        || can.canViewFieldWorkRates
                        || can.canViewFieldWorkFinance,
                    items: [
                        {
                            label: 'Field Work',
                            icon: 'fa-solid fa-people-carry-box',
                            visible: can.canViewFieldWork
                                || can.canViewFieldWorkEquipment
                                || can.canViewFieldWorkRates
                                || can.canViewFieldWorkFinance,
                            items: [
                                {
                                    label: 'Work Packages',
                                    icon: 'fa-solid fa-briefcase',
                                    routerLink: ['/operations/field-work/work-packages'],
                                    visible: can.canViewFieldWork
                                },
                                {
                                    label: 'Field People',
                                    icon: 'fa-solid fa-people-group',
                                    routerLink: ['/operations/field-work/people'],
                                    visible: can.canViewFieldWork
                                },
                                {
                                    label: 'Field Work Setup',
                                    icon: 'fa-solid fa-sliders',
                                    routerLink: ['/operations/field-work/setup'],
                                    visible: can.canViewFieldWork
                                },
                                {
                                    label: 'Schedule',
                                    icon: 'fa-solid fa-calendar-days',
                                    routerLink: ['/operations/field-work/schedule'],
                                    visible: can.canViewFieldWork
                                },
                                {
                                    label: 'Assignments',
                                    icon: 'fa-solid fa-people-group',
                                    routerLink: ['/operations/field-work/assignments'],
                                    visible: can.canViewFieldWork
                                },
                                {
                                    label: 'Approvals',
                                    icon: 'fa-solid fa-circle-check',
                                    routerLink: ['/operations/field-work/approvals'],
                                    visible: can.canViewFieldWork
                                },
                                {
                                    label: 'Tools & Equipment',
                                    icon: 'fa-solid fa-screwdriver-wrench',
                                    routerLink: ['/operations/field-work/equipment'],
                                    visible: can.canViewFieldWorkEquipment
                                },
                                {
                                    label: 'Equipment Rate Cards',
                                    icon: 'fa-solid fa-money-check-dollar',
                                    routerLink: ['/operations/field-work/equipment/rate-cards'],
                                    visible: can.canViewFieldWorkRates
                                },
                                {
                                    label: 'Finance Exceptions',
                                    icon: 'fa-solid fa-triangle-exclamation',
                                    routerLink: ['/operations/field-work/finance-exceptions'],
                                    visible: can.canViewFieldWorkFinance
                                }
                            ]
                        },
                        {
                            label: 'Logistics Projects',
                            icon: 'fa-solid fa-diagram-project',
                            routerLink: ['/logistics-projects'],
                            visible: isProjectsSubscribed && can.canViewLogisticsProjects
                        },
                        {
                            label: 'Project Executions',
                            icon: 'fa-solid fa-file-signature',
                            visible: isProjectsSubscribed && can.canViewLogisticsProjects,
                            items: [
                                {
                                    label: 'Clearance & Forwarding',
                                    icon: 'fa-solid fa-file-signature',
                                    visible: isProjectsSubscribed && can.canViewLogisticsProjects,
                                    items: [
                                        {
                                            label: 'C&F Executions',
                                            icon: 'fa-solid fa-file-signature',
                                            routerLink: ['/cf-executions'],
                                            visible: isProjectsSubscribed && can.canViewLogisticsProjects
                                        },
                                        {
                                            label: 'Supplier & Payment Requests',
                                            icon: 'fa-solid fa-wallet',
                                            routerLink: ['/cf-executions/requests'],
                                            visible: isProjectsSubscribed && can.canViewLogisticsProjects
                                        }
                                    ]
                                },
                                {
                                    label: 'POSM',
                                    icon: 'fa-solid fa-layer-group',
                                    visible: isPosmSubscribed && can.canViewPosmProjects,
                                    items: [
                                        {
                                            label: 'POSM Executions',
                                            icon: 'fa-solid fa-layer-group',
                                            routerLink: ['/posm/executions'],
                                            visible: isPosmSubscribed && can.canViewPosmProjects
                                        },
                                        {
                                            label: 'Delivery Schedules',
                                            icon: 'fa-solid fa-calendar-days',
                                            routerLink: ['/posm/executions/delivery-schedules'],
                                            visible: isPosmSubscribed && can.canViewPosmProjects
                                        },
                                        {
                                            label: 'POSM Setup',
                                            icon: 'fa-solid fa-sliders',
                                            routerLink: ['/posm/setup'],
                                            visible: isPosmSubscribed && can.canViewPosmProjects
                                        }
                                    ]
                                },
                            ]
                        }
                    ]
                },
                {
                    label: 'Warehousing',
                    icon: 'fa-solid fa-boxes-stacked',
                    visible: isWarehousingSubscribed && can.canViewWarehousing,
                    items: [
                        {
                            label: 'Overview',
                            icon: 'fa-solid fa-chart-pie',
                            routerLink: ['/warehousing/overview'],
                            visible: can.canViewWarehousingOverview
                        },
                        {
                            label: 'Items',
                            icon: 'fa-solid fa-box',
                            routerLink: ['/warehousing/items'],
                            visible: can.canViewWarehousingItems
                        },
                        {
                            label: 'Goods In',
                            icon: 'fa-solid fa-dolly',
                            routerLink: ['/warehousing/stock-entry'],
                            visible: can.canViewWarehousingStockEntry
                        },
                        {
                            label: 'On Hand Stock',
                            icon: 'fa-solid fa-boxes-stacked',
                            routerLink: ['/warehousing/stock'],
                            visible: can.canViewWarehousingStock
                        },
                        {
                            label: 'Item Transactions Ledger',
                            icon: 'fa-solid fa-clock-rotate-left',
                            routerLink: ['/warehousing/stock-ledger'],
                            visible: can.canViewWarehousingStock
                        },
                        {
                            label: 'Reservations',
                            icon: 'fa-solid fa-inbox',
                            routerLink: ['/warehousing/reservations'],
                            visible: can.canViewWarehousingStock
                        },
                        {
                            label: 'Goods Out & Dispatches',
                            icon: 'fa-solid fa-truck-ramp-box',
                            routerLink: ['/warehousing/dispatch'],
                            visible: can.canViewWarehousingDispatch
                        },
                        {
                            label: 'Outbound Warehouse Work',
                            icon: 'fa-solid fa-arrow-right-from-bracket',
                            routerLink: ['/warehousing/shipment-work/outbound'],
                            visible: can.canViewWarehousingDispatch || can.canViewWarehousingTransfer
                        },
                        {
                            label: 'Inbound Warehouse Work',
                            icon: 'fa-solid fa-arrow-right-to-bracket',
                            routerLink: ['/warehousing/shipment-work/inbound'],
                            visible: can.canViewWarehousingTransfer
                        },
                        {
                            label: 'Warehouse Transfers',
                            icon: 'fa-solid fa-right-left',
                            routerLink: ['/warehousing/transfers'],
                            visible: can.canViewWarehousingTransfer
                        },
                        {
                            label: 'Refurbishment',
                            icon: 'fa-solid fa-screwdriver-wrench',
                            routerLink: ['/warehousing/refurbishment'],
                            visible: can.canViewWarehousingRefurbishment
                        },
                        {
                            label: 'Destruction',
                            icon: 'fa-solid fa-trash-can',
                            routerLink: ['/warehousing/destruction'],
                            visible: can.canViewWarehousingDestruction
                        },
                        {
                            label: 'Billing Drafts',
                            icon: 'fa-solid fa-file-invoice-dollar',
                            routerLink: ['/warehousing/billing-drafts'],
                            visible: can.canViewWarehousingBilling
                        }
                    ]
                },
                {
                    label: 'Shipments',
                    icon: 'fa-solid fa-truck-ramp-box',
                    visible: can.canViewShipmentSection,
                    items: [
                        {
                            label: 'Shipment Orders',
                            icon: 'fa-solid fa-truck-ramp-box',
                            visible: can.canViewShipmentOrders,
                            items: [
                                {
                                    label: 'Dashboard',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/shipment/dashboard'],
                                    visible: can.canViewShipmentDashboard
                                },
                                {
                                    label: 'Create Shipment',
                                    icon: 'fa-solid fa-folder-plus',
                                    routerLink: ['/shipment/create'],
                                    visible: !isTrialReadOnly && can.canCreateShipment
                                },
                                {
                                    label: 'Express Shipment',
                                    icon: 'fa-solid fa-bolt',
                                    routerLink: ['/shipment/create/express'],
                                    visible: !isTrialReadOnly && can.canCreateShipment
                                },
                                {
                                    label: 'Bulk Upload',
                                    icon: 'fa-solid fa-file-import',
                                    routerLink: ['/shipment/bulk-upload'],
                                    visible: isEnterprise && !isTrialReadOnly && can.canImportShipments
                                },
                                {
                                    label: 'Delivery Rate Cards',
                                    icon: 'fa-solid fa-route',
                                    routerLink: ['/shipment/rate-cards'],
                                    visible: isEnterprise && can.canViewDeliveryRateCards
                                },
                                {
                                    label: 'Internal Transport Costs',
                                    icon: 'fa-solid fa-money-bill-wave',
                                    routerLink: ['/shipment/internal-transport-costs'],
                                    visible: isEnterprise && can.canViewInternalTransportCosts
                                },
                                {
                                    label: 'My Shipments',
                                    icon: 'fa-regular fa-square-caret-right',
                                    routerLink: ['/shipment'],
                                    visible: !isEnterprise && can.canViewShipments,
                                },
                                {
                                    label: 'Shipment Directory',
                                    icon: 'fa-solid fa-truck-fast',
                                    routerLink: ['/shipment/company'],
                                    visible: isEnterprise && can.canViewShipments,
                                },
                                {
                                    label: 'Delivery Directory',
                                    icon: 'fa-solid fa-truck-arrow-right',
                                    routerLink: ['/shipment/deliveries'],
                                    visible: isEnterprise && can.canViewShipmentDeliveries,
                                },
                                {
                                    label: 'Order Releases',
                                    icon: 'fa-solid fa-boxes-stacked',
                                    routerLink: ['/transport-planning/releases'],
                                    visible: isEnterprise && can.canViewTransportReleases,
                                },
                                {
                                    label: 'Cross-Dock Orchestrations',
                                    icon: 'fa-solid fa-network-wired',
                                    routerLink: ['/transport-planning/orchestrations'],
                                    visible: isEnterprise && can.canViewTransportReleases,
                                },
                                {
                                    label: 'Truck Plans',
                                    icon: 'fa-solid fa-route',
                                    routerLink: ['/transport-planning/plans'],
                                    visible: isEnterprise && can.canViewTransportPlans,
                                },
                                {
                                    label: 'Bulk Planning Runs',
                                    icon: 'fa-solid fa-layer-group',
                                    routerLink: ['/transport-planning/bulk-planning'],
                                    visible: isEnterprise && can.canViewTransportPlans,
                                },
                                {
                                    label: 'Run Bulk Plan',
                                    icon: 'fa-solid fa-truck-fast',
                                    routerLink: ['/transport-planning/bulk-planning/new'],
                                    visible: isEnterprise && can.canCreateTransportPlans,
                                }
                            ]
                        },
                        {
                            label: 'Customs Clearance',
                            icon: 'fa-solid fa-school-circle-check',
                            routerLink: can.canViewClearance ? ['/clearance'] : undefined,
                            visible: isCustomsClearanceSubscribed && can.canViewCustomsClearance,
                            items: [
                                {
                                    label: 'Create Clearance',
                                    icon: 'fa-solid fa-plus',
                                    routerLink: ['/clearance/add'],
                                    visible: can.canCreateClearance
                                },
                                {
                                    label: 'Clearances',
                                    icon: 'fa-solid fa-folder-plus',
                                    routerLink: ['/clearance/list'],
                                    visible: can.canViewClearance
                                },
                                {
                                    label: 'Clearance Services',
                                    icon: 'fa-regular fa-square-caret-right',
                                    routerLink: ['/clearance/services'],
                                    visible: can.canViewClearanceServices
                                }
                            ]
                        },
                        {
                            label: 'Container Tracking',
                            icon: 'fa-solid fa-box-open',
                            routerLink: can.canViewContainerDashboard ? ['/container-tracking'] : undefined,
                            visible: isContainerTrackingSubscribed && can.canViewContainerTracking,
                            items: [
                                {
                                    label: 'Overview',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/container-tracking/overview'],
                                    visible: can.canViewContainerDashboard
                                },
                                {
                                    label: 'Cases',
                                    icon: 'fa-solid fa-boxes-stacked',
                                    routerLink: ['/container-tracking/cases'],
                                    visible: can.canViewContainerCases
                                },
                                {
                                    label: 'Yard Control Board',
                                    icon: 'fa-solid fa-table-cells',
                                    routerLink: ['/container-tracking/inventory'],
                                    visible: can.canViewContainerSetup
                                },
                                {
                                    label: 'Truck Appointments',
                                    icon: 'fa-regular fa-calendar-check',
                                    routerLink: ['/container-tracking/appointments'],
                                    visible: can.canViewContainerSetup
                                },
                                {
                                    label: 'EIR & Condition',
                                    icon: 'fa-regular fa-clipboard',
                                    routerLink: ['/container-tracking/eirs'],
                                    visible: can.canViewContainerSetup
                                },
                                {
                                    label: 'Setup',
                                    icon: 'fa-solid fa-warehouse',
                                    visible: can.canViewContainerSetup,
                                    items: [
                                        {
                                            label: 'Setup Home',
                                            icon: 'fa-solid fa-sitemap',
                                            routerLink: ['/container-tracking/setup'],
                                            visible: can.canViewContainerSetup
                                        },
                                        {
                                            label: 'Terminal Facilities',
                                            icon: 'fa-solid fa-building',
                                            routerLink: ['/container-tracking/setup/facilities'],
                                            visible: can.canViewContainerSetup
                                        },
                                        {
                                            label: 'Yard Areas',
                                            icon: 'fa-solid fa-layer-group',
                                            routerLink: ['/container-tracking/setup/areas'],
                                            visible: can.canViewContainerSetup
                                        },
                                        {
                                            label: 'Yard Slots',
                                            icon: 'fa-solid fa-table-cells-large',
                                            routerLink: ['/container-tracking/setup/yard-slots'],
                                            visible: can.canViewContainerSetup
                                        },
                                        {
                                            label: 'Yard Charge Rules',
                                            icon: 'fa-solid fa-coins',
                                            routerLink: ['/container-tracking/setup/charge-rules'],
                                            visible: can.canViewContainerSetup
                                        },
                                        {
                                            label: 'Execution Values',
                                            icon: 'fa-solid fa-list-check',
                                            routerLink: ['/container-tracking/setup/execution-values'],
                                            visible: can.canViewContainerSetup
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                },
                {
                    label: 'Fleet',
                    icon: 'fa-solid fa-truck-fast',
                    routerLink: can.canViewFleetDashboard ? ['/fleet'] : undefined,
                    visible: isFleetSubscribed && can.canViewFleet,
                    items: [
                        {
                            label: 'Assets',
                            icon: 'fa-regular fa-id-card',
                            visible: can.canViewFleetDrivers,
                            items: [
                                {
                                    label: 'Truck P&L',
                                    icon: 'fa-solid fa-chart-pie',
                                    routerLink: ['/fleet/truck-profitability'],
                                    visible: can.canViewFleetProfitability
                                },
                                {
                                    label: 'Operational Profitability',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/fleet/profitability'],
                                    visible: can.canViewFleetProfitability
                                },
                                {
                                    label: 'Drivers',
                                    icon: 'fa-regular fa-id-card',
                                    routerLink: ['/fleet/drivers'],
                                    visible: can.canViewFleetDrivers
                                },
                                {
                                    label: 'Vehicles',
                                    icon: 'fa-solid fa-truck-moving',
                                    routerLink: ['/fleet/vehicles'],
                                    visible: can.canViewFleetVehicles
                                },
                                {
                                    label: 'Trailers',
                                    icon: 'fa-solid fa-trailer',
                                    routerLink: ['/fleet/trailers'],
                                    visible: can.canViewFleetTrailers
                                },
                                {
                                    label: 'Trailer Types',
                                    icon: 'fa-solid fa-tags',
                                    routerLink: ['/fleet/trailer-types'],
                                    visible: can.canViewFleetTrailerTypes
                                },
                                {
                                    label: 'Documents',
                                    icon: 'fa-solid fa-folder-open',
                                    routerLink: ['/fleet/documents'],
                                    visible: can.canViewFleetDocuments
                                },
                            ]
                        },
                        {
                            label: 'Rental',
                            icon: 'fa-solid fa-file-invoice-dollar',
                            routerLink: can.canViewFleetRentals ? ['/fleet/rentals'] : undefined,
                            visible: can.canViewFleetRentals,
                            items: [
                                {
                                    label: 'Overview',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/fleet/rentals'],
                                    visible: can.canViewFleetRentals
                                },
                                {
                                    label: 'Agreements',
                                    icon: 'fa-solid fa-file-signature',
                                    routerLink: ['/fleet/rentals/agreements'],
                                    visible: can.canViewFleetRentals
                                },
                                {
                                    label: 'Usage & Exceptions',
                                    icon: 'fa-solid fa-clipboard-check',
                                    routerLink: ['/fleet/rentals/usage'],
                                    visible: can.canViewFleetRentalUsage
                                },
                                {
                                    label: 'Rental Settlements',
                                    icon: 'fa-solid fa-money-check-dollar',
                                    routerLink: ['/fleet/rentals/settlements'],
                                    visible: can.canViewFleetRentalSettlements
                                }
                            ]
                        },
                        {
                            label: 'GPS Tracking',
                            icon: 'fa-solid fa-location-crosshairs',
                            routerLink: can.canViewFleetTelematics ? ['/fleet/gps'] : undefined,
                            visible: isGpsTrackingSubscribed && can.canViewFleetGps,
                            items: [
                                {
                                    label: 'Live Control Tower',
                                    icon: 'pi pi-map-marker',
                                    routerLink: ['/fleet/gps'],
                                    visible: can.canViewFleetTelematics
                                },
                                {
                                    label: 'Provider Marketplace',
                                    icon: 'pi pi-sitemap',
                                    routerLink: ['/fleet/gps/providers'],
                                    visible: can.canManageFleetTelematics
                                },
                                {
                                    label: 'Device Mapping',
                                    icon: 'pi pi-link',
                                    routerLink: ['/fleet/gps/mappings'],
                                    visible: can.canManageFleetTelematics
                                },
                                {
                                    label: 'Geofences',
                                    icon: 'pi pi-map',
                                    routerLink: ['/fleet/gps/geofences'],
                                    visible: can.canManageFleetTelematics
                                },
                                {
                                    label: 'Alerts',
                                    icon: 'pi pi-bell',
                                    routerLink: ['/fleet/gps/alerts'],
                                    visible: can.canManageFleetTelematics
                                },
                                {
                                    label: 'Reports',
                                    icon: 'pi pi-chart-bar',
                                    routerLink: ['/fleet/gps/reports'],
                                    visible: can.canViewFleetTelematics
                                }
                            ]
                        },
                        {
                            label: 'Mobilization',
                            icon: 'fa-solid fa-diagram-project',
                            routerLink: can.canViewMobilizationOverview ? ['/fleet/mobilization'] : undefined,
                            visible: can.canViewFleetMobilization,
                            items: [
                                {
                                    label: 'Overview',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/fleet/mobilization'],
                                    visible: can.canViewMobilizationOverview
                                },
                                {
                                    label: 'Entries',
                                    icon: 'fa-solid fa-list-ul',
                                    routerLink: ['/fleet/mobilization/entries'],
                                    visible: can.canViewMobilizationEntries
                                },
                                {
                                    label: 'Planning & Tracking',
                                    icon: 'fa-solid fa-route',
                                    routerLink: ['/fleet/mobilization/planning'],
                                    visible: can.canViewMobilizationPlanning
                                },
                                {
                                    label: 'New Entry',
                                    icon: 'fa-solid fa-plus',
                                    routerLink: ['/fleet/mobilization/new'],
                                    visible: can.canCreateMobilizationEntry
                                },
                                {
                                    label: 'Change Requests',
                                    icon: 'fa-solid fa-retweet',
                                    routerLink: ['/fleet/mobilization/change-requests'],
                                    visible: can.canViewMobilizationChangeRequests
                                },
                                {
                                    label: 'History',
                                    icon: 'fa-solid fa-history',
                                    routerLink: ['/fleet/mobilization/history'],
                                    visible: can.canViewMobilizationHistory
                                },
                                {
                                    label: 'Timesheet Grid',
                                    icon: 'fa-solid fa-calendar-days',
                                    routerLink: ['/fleet/mobilization/timesheet-grid'],
                                    visible: can.canViewMobilizationTimesheets
                                },
                                {
                                    label: 'Salary Rules',
                                    icon: 'fa-solid fa-wallet',
                                    routerLink: ['/fleet/mobilization/salary-rules'],
                                    visible: can.canViewMobilizationSalaryRules
                                }
                            ]
                        },
                        {
                            label: 'Maintenance',
                            icon: 'fa-solid fa-screwdriver-wrench',
                            routerLink: can.canViewMaintenanceOverview ? ['/fleet/maintenance'] : undefined,
                            visible: can.canViewFleetMaintenance,
                            items: [
                                {
                                    label: 'Maintenance Overview',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/fleet/maintenance'],
                                    visible: can.canViewMaintenanceOverview
                                },
                                {
                                    label: 'Work Orders',
                                    icon: 'fa-solid fa-clipboard-list',
                                    routerLink: ['/fleet/maintenance/work-orders'],
                                    visible: can.canViewMaintenanceWorkOrders
                                },
                                {
                                    label: 'Preventive Maintenance',
                                    icon: 'fa-solid fa-calendar-check',
                                    routerLink: ['/fleet/maintenance/preventive'],
                                    visible: can.canViewMaintenancePreventive
                                },
                                {
                                    label: 'Inspections & Defects',
                                    icon: 'fa-solid fa-clipboard-check',
                                    routerLink: ['/fleet/maintenance/inspections'],
                                    visible: can.canViewMaintenanceInspections
                                },
                                {
                                    label: 'Parts & Inventory',
                                    icon: 'fa-solid fa-boxes-stacked',
                                    routerLink: ['/fleet/maintenance/parts'],
                                    visible: can.canViewMaintenanceParts
                                },
                                {
                                    label: 'Availability Board',
                                    icon: 'fa-solid fa-shield-halved',
                                    routerLink: ['/fleet/maintenance/availability'],
                                    visible: can.canViewMaintenanceAvailability
                                },
                                {
                                    label: 'Analytics & Alerts',
                                    icon: 'fa-solid fa-chart-simple',
                                    routerLink: ['/fleet/maintenance/analytics'],
                                    visible: can.canViewMaintenanceAnalytics
                                }
                            ]
                        },
                        {
                            label: 'Fuel Management',
                            icon: 'fa-solid fa-gas-pump',
                            routerLink: ['/fuel-management'],
                            visible: isFuelManagementSubscribed && can.canViewFuelManagement,
                            items: [
                                {
                                    label: 'Overview',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/fuel-management'],
                                    visible: can.canViewFuelManagementOverview
                                },
                                {
                                    label: 'Marketplace',
                                    icon: 'fa-solid fa-store',
                                    routerLink: ['/fuel-management/marketplace'],
                                    visible: can.canViewFuelMarketplace
                                },
                                {
                                    label: 'Connections',
                                    icon: 'fa-solid fa-plug',
                                    routerLink: ['/fuel-management/connections'],
                                    visible: can.canManageFuelConnections
                                },
                                {
                                    label: 'Transactions',
                                    icon: 'fa-solid fa-gas-pump',
                                    routerLink: ['/fuel-management/transactions'],
                                    visible: can.canViewFuelTransactions || can.canReconcileFuelTransactions
                                },
                                {
                                    label: 'Reports',
                                    icon: 'fa-solid fa-chart-column',
                                    routerLink: ['/fuel-management/reports'],
                                    visible: can.canViewFuelReports
                                }
                            ]
                        }
                    ]
                },
                {
                    label: 'Finance',
                    icon: 'fa-solid fa-cash-register',
                    visible: can.canViewFinance,
                    items: [
                        {
                            label: 'Dashboard',
                            icon: 'fa-solid fa-gauge-high',
                            visible: isEnterprise && can.canViewFinanceDashboard,
                            routerLink: ['/accounts']
                        },
                        {
                            label: 'Chart of Accounts',
                            icon: 'fa-solid fa-chart-bar',
                            visible: isEnterprise && can.canViewChartOfAccounts,
                            routerLink: ['/chart-of-accounts']
                        },
                        {
                            label: 'Workforce Finance',
                            icon: 'fa-solid fa-id-card-clip',
                            routerLink: ['/accounts/enterprise/workforce-finance'],
                            visible: isEnterprise && can.canViewWorkforceFinance
                        },
                        {
                            label: 'Orders',
                            icon: 'fa-solid fa-cart-flatbed',
                            visible: isEnterprise && can.canViewOrders,
                            items: [
                                {
                                    label: 'Sales Orders',
                                    icon: 'fa-solid fa-square-caret-up',
                                    routerLink: ['/order/sales-order'],
                                    visible: can.canViewSalesOrders
                                },
                                {
                                    label: 'Purchase Orders',
                                    icon: 'fa-solid fa-square-caret-down',
                                    routerLink: ['order/purchase-order'],
                                    visible: can.canViewPurchaseOrders
                                },
                            ]
                        },
                        {
                            label: 'Expenses',
                            icon: 'fa-solid fa-money-bill-wave',
                            visible: isEnterprise && can.canViewFinanceExpenses,
                            items: [
                                {
                                    label: 'List Expenses',
                                    icon: 'fa-solid fa-list-ul',
                                    routerLink: ['/expense'],
                                    visible: can.canViewExpenses
                                },
                                {
                                    label: 'Create Expense',
                                    icon: 'fa-solid fa-plus',
                                    routerLink: ['/expense/add'],
                                    visible: can.canCreateExpense
                                },
                                {
                                    label: 'Approve Expense',
                                    icon: 'fa-solid fa-person-circle-check',
                                    routerLink: ['/expense/approvals'],
                                    visible: can.canViewExpenseApprovals
                                },
                                {
                                    label: 'Post Expense',
                                    icon: 'fa-solid fa-paper-plane',
                                    routerLink: ['/expense/post'],
                                    visible: can.canViewExpensePosting
                                },
                            ]
                        },
                        {
                            label: 'Receivables',
                            icon: 'fa-solid fa-circle-down',
                            visible: isEnterprise && can.canViewReceivables,
                            items: [
                                {
                                    label: 'Account Receivables',
                                    icon: 'fa-solid fa-file-invoice',
                                    routerLink: ['/accounts/enterprise/receivables/to-be-invoiced'],
                                    visible: can.canViewReceivablesToInvoice
                                },
                                {
                                    label: 'Manual Receivable Line',
                                    icon: 'fa-solid fa-pen-to-square',
                                    routerLink: ['/accounts/enterprise/receivables/to-be-invoiced/manual'],
                                    visible: can.canCreateManualReceivableLine
                                },
                                {
                                    label: 'Customer Invoices',
                                    icon: 'fa-solid fa-file-invoice',
                                    routerLink: ['/accounts/enterprise/receivables/generated-invoices'],
                                    visible: can.canViewCustomerInvoices
                                },
                                {
                                    label: 'Invoice Dispatch',
                                    icon: 'fa-solid fa-envelope-open-text',
                                    routerLink: ['/accounts/enterprise/receivables/invoice-dispatches'],
                                    visible: can.canViewCustomerInvoices
                                },
                                {
                                    label: 'Manual Invoice Draft',
                                    icon: 'fa-solid fa-file-circle-plus',
                                    routerLink: ['/accounts/enterprise/receivables/generated-invoices/manual-draft'],
                                    visible: can.canCreateManualInvoiceDraft
                                },
                                {
                                    label: 'Customer Receipts',
                                    icon: 'fa-solid fa-receipt',
                                    routerLink: ['/accounts/enterprise/receivables/generated-receipts'],
                                    visible: can.canViewCustomerReceipts
                                },
                                {
                                    label: 'Customer Credit Notes',
                                    icon: 'fa-solid fa-arrow-down-wide-short',
                                    routerLink: ['/accounts/enterprise/receivables/customer-credit-note'],
                                    visible: this.permissionService.getPermissionCodes().has('accounts.customer-credit-note.view')
                                },
                                {
                                    label: 'Customer Debit Notes',
                                    icon: 'fa-solid fa-arrow-up-short-wide',
                                    routerLink: ['/accounts/enterprise/receivables/debit-note'],
                                    visible: can.canViewDebitNotes
                                }
                            ]
                        },
                        {
                            label: 'Payables',
                            icon: 'fa-solid fa-circle-up',
                            visible: isEnterprise && can.canViewPayables,
                            items: [
                                {
                                    label: 'Account Payables',
                                    icon: 'fa-solid fa-file-invoice',
                                    routerLink: ['/accounts/enterprise/payables/to-be-billed'],
                                    visible: can.canViewPayablesToBill
                                },
                                {
                                    label: 'Bulk Costs',
                                    icon: 'fa-solid fa-layer-group',
                                    routerLink: ['/accounts/enterprise/payables/bulk-costs'],
                                    visible: can.canViewPayablesToBill
                                },
                                {
                                    label: 'Manual Payable Line',
                                    icon: 'fa-solid fa-pen-to-square',
                                    routerLink: ['/accounts/enterprise/payables/to-be-billed/manual'],
                                    visible: can.canCreateManualPayableLine
                                },
                                {
                                    label: 'Vendor Bills',
                                    icon: 'fa-solid fa-file-invoice',
                                    routerLink: ['/accounts/enterprise/payables/generated-bills'],
                                    visible: can.canViewVendorBills
                                },
                                {
                                    label: 'AP Matching',
                                    icon: 'fa-solid fa-scale-balanced',
                                    routerLink: ['/accounts/enterprise/payables/matching-dashboard'],
                                    visible: can.canViewApMatching
                                },
                                {
                                    label: 'Matching Policies',
                                    icon: 'fa-solid fa-sliders',
                                    routerLink: ['/accounts/enterprise/payables/matching-policies'],
                                    visible: can.canManageMatchingPolicies
                                },
                                {
                                    label: 'Payment Proposals',
                                    icon: 'fa-solid fa-money-check-dollar',
                                    routerLink: ['/accounts/enterprise/payables/payment-proposals'],
                                    visible: can.canViewPaymentProposals
                                },
                                {
                                    label: 'Vendor Statements',
                                    icon: 'fa-solid fa-file-lines',
                                    routerLink: ['/accounts/enterprise/payables/vendor-statement-reconciliation'],
                                    visible: can.canViewVendorStatements
                                },
                                {
                                    label: 'Non-PO Bill Draft',
                                    icon: 'fa-solid fa-file-circle-plus',
                                    routerLink: ['/accounts/enterprise/payables/generated-bills/non-po-draft'],
                                    visible: can.canCreateNonPoBillDraft
                                },
                                {
                                    label: 'Vouchers',
                                    icon: 'fa-solid fa-receipt',
                                    routerLink: ['/accounts/enterprise/payables/generated-vouchers'],
                                    visible: can.canViewVouchers
                                },
                                {
                                    label: 'Vendor Debit Claims',
                                    icon: 'fa-solid fa-arrow-up-short-wide',
                                    routerLink: ['/accounts/enterprise/payables/vendor-debit-note'],
                                    visible: this.permissionService.getPermissionCodes().has('accounts.vendor-debit-note.view')
                                },
                                {
                                    label: 'Vendor Credit Notes',
                                    icon: 'fa-solid fa-arrow-down-wide-short',
                                    routerLink: ['/accounts/enterprise/payables/credit-note'],
                                    visible: can.canViewCreditNotes
                                }
                            ]
                        },
                        {
                            label: 'Banking',
                            icon: 'fa-solid fa-landmark',
                            visible: isEnterprise && can.canViewBanking,
                            items: [
                                {
                                    label: 'Bank Accounts',
                                    icon: 'fa-solid fa-wallet',
                                    routerLink: ['/accounts/enterprise/banking/bank-accounts'],
                                    visible: can.canViewBankAccounts
                                },
                                {
                                    label: 'Banks',
                                    icon: 'fa-solid fa-building-columns',
                                    routerLink: ['/accounts/enterprise/banking/banks'],
                                    visible: can.canViewBanks
                                },
                                {
                                    label: 'Payment Transactions',
                                    icon: 'fa-solid fa-money-check-dollar',
                                    routerLink: ['/accounts/enterprise/payment-transactions'],
                                    visible: can.canViewPaymentTransactions
                                },
                                {
                                    label: 'Bank Reconciliation Dashboard',
                                    icon: 'fa-solid fa-scale-balanced',
                                    routerLink: ['/accounts/enterprise/bank-reconciliation/dashboard'],
                                    visible: can.canViewBankReconciliationDashboard
                                },
                                {
                                    label: 'Bank Statements',
                                    icon: 'fa-solid fa-building-columns',
                                    routerLink: ['/accounts/enterprise/bank-reconciliation/statements'],
                                    visible: can.canViewBankStatements
                                },
                                {
                                    label: 'Reconciliation Reports',
                                    icon: 'fa-solid fa-chart-line',
                                    routerLink: ['/accounts/enterprise/bank-reconciliation/reports'],
                                    visible: can.canViewReconciliationReports
                                },
                                {
                                    label: 'Reconciliation Rules',
                                    icon: 'fa-solid fa-wand-magic-sparkles',
                                    routerLink: ['/accounts/enterprise/bank-reconciliation/rules'],
                                    visible: can.canManageReconciliationRules
                                },
                                {
                                    label: 'Reconciliation Exceptions',
                                    icon: 'fa-solid fa-triangle-exclamation',
                                    routerLink: ['/accounts/enterprise/bank-reconciliation/exceptions'],
                                    visible: can.canManageReconciliationExceptions
                                }
                            ]
                        },
                        {
                            label: 'Accounting',
                            icon: 'fa-solid fa-book',
                            visible: isEnterprise && can.canViewAccounting,
                            items: [
                                {
                                    label: 'Journal Entries',
                                    icon: 'fa-solid fa-book-open-reader',
                                    routerLink: ['/accounts/enterprise/journal-entries'],
                                    visible: can.canViewJournalEntries
                                },
                                {
                                    label: 'Cost Centers',
                                    icon: 'fa-solid fa-tags',
                                    routerLink: ['/accounts/enterprise/cost-center'],
                                    visible: can.canViewCostCenters
                                },
                                {
                                    label: 'Accounting Controls',
                                    icon: 'fa-solid fa-shield-halved',
                                    visible: can.canViewAccountingControls,
                                    items: [
                                        {
                                            label: 'Period Locks',
                                            icon: 'fa-solid fa-lock',
                                            routerLink: ['/accounts/enterprise/admin/accounting-period-locks'],
                                            visible: can.canManageAccountingPeriodLocks
                                        },
                                        {
                                            label: 'Manual Entry Controls',
                                            icon: 'fa-solid fa-sliders',
                                            routerLink: ['/accounts/enterprise/admin/manual-entry-controls'],
                                            visible: can.canManageManualEntryControls
                                        },
                                    ]
                                },
                                {
                                    label: 'Ledgers',
                                    icon: 'fa-solid fa-rss',
                                    visible: can.canViewLedgers,
                                    items: [
                                        {
                                            label: 'General Ledger',
                                            icon: 'fa-regular fa-note-sticky',
                                            routerLink: ['/accounts/enterprise/ledgers/general-ledger'],
                                            visible: can.canViewGeneralLedger
                                        },
                                        {
                                            label: 'Partner Ledger',
                                            icon: 'fa-regular fa-clipboard',
                                            routerLink: ['/accounts/enterprise/ledgers/partner-ledger'],
                                            visible: can.canViewPartnerLedger
                                        },
                                    ]
                                },
                            ]
                        },
                        {
                            label: 'Reports',
                            icon: 'fa-solid fa-chart-pie',
                            visible: isEnterprise && can.canViewFinanceReports,
                            items: [
                                {
                                    label: 'Profit & Loss',
                                    icon: 'fa-regular fa-note-sticky',
                                    routerLink: ['/accounts/enterprise/reports/profit-and-loss'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Balance Sheet',
                                    icon: 'fa-regular fa-clipboard',
                                    routerLink: ['/accounts/enterprise/reports/balance-sheet'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Trial Balance',
                                    icon: 'fa-solid fa-scale-balanced',
                                    routerLink: ['/accounts/enterprise/reports/trial-balance'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Tax Report',
                                    icon: 'fa-solid fa-landmark',
                                    routerLink: ['/accounts/enterprise/reports/tax-report'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Sales Register',
                                    icon: 'fa-solid fa-table-list',
                                    routerLink: ['/accounts/enterprise/reports/sales-register'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'E-Invoice Compliance',
                                    icon: 'fa-solid fa-shield-halved',
                                    routerLink: ['/accounts/enterprise/compliance/e-invoices'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Aged Receivable',
                                    icon: 'fa-solid fa-filter-circle-dollar',
                                    routerLink: ['/accounts/enterprise/reports/aged-receivable'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Aged Payable',
                                    icon: 'fa-solid fa-money-bill-transfer',
                                    routerLink: ['/accounts/enterprise/reports/aged-payable'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Cash Flow',
                                    icon: 'fa-solid fa-money-bill-trend-up',
                                    routerLink: ['/accounts/enterprise/reports/cash-flow'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Executive Summary',
                                    icon: 'fa-solid fa-gauge-high',
                                    routerLink: ['/accounts/enterprise/reports/executive-summary'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Customer Statements',
                                    icon: 'fa-solid fa-file-invoice-dollar',
                                    routerLink: ['/accounts/enterprise/reports/customer-statements'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Supplier Statements',
                                    icon: 'fa-solid fa-file-invoice',
                                    routerLink: ['/accounts/enterprise/reports/supplier-statements'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'AR Register & Payments',
                                    icon: 'fa-solid fa-arrow-down-wide-short',
                                    routerLink: ['/accounts/enterprise/reports/ar-register'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'AP Register & Payments',
                                    icon: 'fa-solid fa-arrow-up-wide-short',
                                    routerLink: ['/accounts/enterprise/reports/ap-register'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Bank & Cash',
                                    icon: 'fa-solid fa-building-columns',
                                    routerLink: ['/accounts/enterprise/reports/bank-cash'],
                                    visible: can.canViewFinanceReports
                                },
                                {
                                    label: 'Fixed Assets',
                                    icon: 'fa-solid fa-truck',
                                    routerLink: ['/accounts/enterprise/reports/fixed-assets'],
                                    visible: can.canViewFinanceReports
                                },
                            ]
                        },
                        {
                            label: 'Accounts Payable',
                            icon: 'fa-solid fa-money-bill-transfer',
                            visible: !isEnterprise && can.canViewAccountsPayable,
                            items: [
                                {
                                    label: 'AP Invoices',
                                    icon: 'fa-solid fa-file-invoice',
                                    routerLink: ['/accounts/invoice'],
                                    visible: can.canViewAccountsPayableInvoices
                                },
                                {
                                    label: 'AP Receipts',
                                    icon: 'fa-solid fa-receipt',
                                    routerLink: ['/accounts/receipt'],
                                    visible: can.canViewAccountsPayableReceipts
                                }
                            ]
                        }
                    ]
                },
                {
                    label: 'HR',
                    icon: 'fa-solid fa-users-gear',
                    visible: isHrSubscribed && can.canViewHr,
                    items: [
                        {
                            label: 'Dashboard',
                            icon: 'fa-solid fa-chart-line',
                            routerLink: ['/hr'],
                            visible: can.canViewHrDashboard
                        },
                        {
                            label: 'Employees',
                            icon: 'fa-solid fa-users',
                            routerLink: ['/hr/employees'],
                            visible: can.canViewHrEmployees
                        },
                        {
                            label: 'Onboarding',
                            icon: 'fa-solid fa-user-plus',
                            routerLink: ['/hr/onboarding'],
                            visible: can.canViewHrOnboarding
                        },
                        {
                            label: 'Recruitment',
                            icon: 'fa-solid fa-user-magnifying-glass',
                            routerLink: ['/hr/recruitment'],
                            visible: can.canViewHrRecruitment
                        },
                        {
                            label: 'Learning',
                            icon: 'fa-solid fa-graduation-cap',
                            routerLink: ['/hr/learning'],
                            visible: can.canViewHrLearning
                        },
                        {
                            label: 'Performance',
                            icon: 'fa-solid fa-chart-line',
                            routerLink: ['/hr/performance'],
                            visible: can.canViewHrPerformance
                        },
                        {
                            label: 'Cases',
                            icon: 'fa-solid fa-briefcase',
                            routerLink: ['/hr/cases'],
                            visible: can.canViewHrCases
                        },
                        {
                            label: 'Analytics',
                            icon: 'fa-solid fa-chart-pie',
                            routerLink: ['/hr/analytics'],
                            visible: can.canViewHrAnalytics
                        },
                        {
                            label: 'Annual HR Report',
                            icon: 'fa-solid fa-table-columns',
                            routerLink: ['/hr/analytics/annual-report'],
                            visible: can.canViewHrAnalytics
                        },
                        {
                            label: 'Lifecycle',
                            icon: 'fa-solid fa-arrows-rotate',
                            routerLink: ['/hr/lifecycle'],
                            visible: can.canViewHrLifecycle
                        },
                        {
                            label: 'Documents',
                            icon: 'fa-solid fa-folder-open',
                            routerLink: ['/hr/documents'],
                            visible: can.canViewHrDocuments
                        },
                        {
                            label: 'Admin Operations',
                            icon: 'fa-solid fa-building-shield',
                            routerLink: ['/hr/admin-operations'],
                            visible: can.canViewHrAdminOperations
                        },
                        {
                            label: 'Document Types',
                            icon: 'fa-solid fa-sliders',
                            routerLink: ['/hr/documents/types'],
                            visible: can.canViewHrDocumentTypes
                        },
                        {
                            label: 'Readiness',
                            icon: 'fa-solid fa-clipboard-check',
                            routerLink: ['/hr/readiness'],
                            visible: can.canViewHrReadiness
                        },
                        {
                            label: 'Leave',
                            icon: 'fa-solid fa-calendar-days',
                            routerLink: ['/hr/leave'],
                            visible: can.canViewHrLeave
                        },
                        {
                            label: 'Time',
                            icon: 'fa-solid fa-business-time',
                            routerLink: ['/hr/time'],
                            visible: can.canViewHrTime
                        },
                        {
                            label: 'Biometric',
                            icon: 'fa-solid fa-fingerprint',
                            routerLink: ['/hr/biometric'],
                            visible: can.canViewHrBiometric
                        },
                        {
                            label: 'Country Packs',
                            icon: 'fa-solid fa-globe',
                            routerLink: ['/hr/country-packs'],
                            visible: can.canViewHrCountryPacks
                        },
                        {
                            label: 'Roster',
                            icon: 'fa-solid fa-calendar-week',
                            routerLink: ['/hr/roster'],
                            visible: can.canViewHrRoster
                        },
                        {
                            label: 'Payroll',
                            icon: 'fa-solid fa-file-invoice-dollar',
                            routerLink: ['/hr/payroll'],
                            visible: can.canViewHrPayroll
                        },
                        {
                            label: 'Setup',
                            icon: 'fa-solid fa-sitemap',
                            routerLink: ['/hr/setup'],
                            visible: can.canViewHrSetup
                        },
                        {
                            label: 'Migration',
                            icon: 'fa-solid fa-code-merge',
                            routerLink: ['/hr/migration'],
                            visible: can.canViewHrMigration
                        }
                    ]
                },
                {
                    label: 'Audit',
                    icon: 'fa-solid fa-clipboard-check',
                    routerLink: ['/audit'],
                    visible: isAuditSubscribed && can.canViewAudit
                },
                {
                    label: 'Administration',
                    icon: 'fa-solid fa-gear',
                    visible: can.canViewAdministration,
                    items: [
                        {
                            label: 'Subscription Management',
                            icon: 'fa-solid fa-credit-card',
                            routerLink: ['/enterprise-subscription/manage'],
                            visible: can.canManageSubscription
                        },
                        {
                            label: 'Government Onboarding',
                            icon: 'fa-solid fa-shield',
                            routerLink: ['/accounts/enterprise/compliance'],
                            visible: can.canViewFinanceReports
                        },
                        {
                            label: 'Company Setup',
                            icon: 'fa-solid fa-building',
                            visible: can.canViewCompanySetup,
                            items: [
                                {
                                    label: 'Users & Roles',
                                    icon: 'fa-solid fa-users-gear',
                                    routerLink: ['/company/users-and-roles'],
                                    visible: can.canViewCompanySettings
                                },
                                {
                                    label: 'Company Settings',
                                    icon: 'fa-solid fa-building-circle-check',
                                    routerLink: ['/company'],
                                    visible: can.canViewCompanySettings
                                },
                                {
                                    label: 'Company Groups',
                                    icon: 'fa-solid fa-sitemap',
                                    routerLink: ['/company/groups'],
                                    visible: can.canViewCompanyGroups
                                },
                                {
                                    label: 'Regions',
                                    icon: 'fa-solid fa-map',
                                    routerLink: ['/setup/regions'],
                                    visible: can.canViewRegions
                                },
                                {
                                    label: 'Location Zones',
                                    icon: 'fa-solid fa-draw-polygon',
                                    routerLink: ['/setup/location-zones'],
                                    visible: can.canViewLocationZones
                                },
                                {
                                    label: 'Warehouses',
                                    icon: 'fa-solid fa-warehouse',
                                    routerLink: ['/setup/warehouses'],
                                    visible: can.canViewWarehouses
                                },
                                {
                                    label: 'Locations',
                                    icon: 'fa-solid fa-location-pin',
                                    routerLink: ['/location'],
                                    visible: can.canViewLocations
                                },
                                {
                                    label: 'Master Data Import',
                                    icon: 'fa-solid fa-file-import',
                                    routerLink: ['/setup/master-data-import'],
                                    visible: can.canManageTenantMasterDataImport
                                }
                            ]
                        },
                        {
                            label: 'Finance Setup',
                            icon: 'fa-solid fa-sliders',
                            visible: isEnterprise && can.canViewFinanceSetup,
                            items: [
                                {
                                    label: 'Products',
                                    icon: 'fa-solid fa-boxes-stacked',
                                    routerLink: ['/order/products'],
                                    visible: can.canViewProducts
                                },
                                {
                                    label: 'Expense Workflow Engine',
                                    icon: 'fa-solid fa-money-bill-trend-up',
                                    routerLink: ['/workflow/config'],
                                    queryParams: { resourceKey: 'EXPENSE' },
                                    visible: can.canConfigureExpenseWorkflow
                                }
                            ]
                        },
                        {
                            label: 'System Setup',
                            icon: 'fa-solid fa-diagram-project',
                            visible: ((isEnterprise || isWorkflowAutomationSubscribed || isWasenderSubscribed || isFuelManagementSubscribed || isApiPlatformSubscribed)
                                && can.canViewSystemSetup),
                            items: [
                                {
                                    label: 'Workflow Engine',
                                    icon: 'fa-solid fa-diagram-project',
                                    routerLink: ['/workflow/config'],
                                    visible: (isEnterprise || isWorkflowAutomationSubscribed) && can.canManageWorkflow
                                },
                                {
                                    label: 'Flex Fields',
                                    icon: 'fa-solid fa-note-sticky',
                                    routerLink: ['/flex-fields/config'],
                                    visible: (isEnterprise || isWorkflowAutomationSubscribed) && can.canManageFlexFields
                                },
                                {
                                    label: 'WhatsApp Numbers',
                                    icon: 'pi pi-whatsapp',
                                    routerLink: ['/wasender/setup'],
                                    visible: isWasenderSubscribed && can.canViewWasenderSetup
                                },
                                {
                                    label: 'Business Events',
                                    icon: 'fa-solid fa-bolt',
                                    routerLink: ['/business-events/alerts'],
                                    visible: (isWorkflowAutomationSubscribed || isWasenderSubscribed) && can.canViewBusinessEvents
                                },
                                {
                                    label: 'Developer Platform',
                                    icon: 'fa-solid fa-code',
                                    routerLink: ['/developer-platform'],
                                    visible: isApiPlatformSubscribed && can.canViewDeveloperPlatform
                                },
                                {
                                    label: 'Fuel Apps',
                                    icon: 'fa-solid fa-gas-pump',
                                    routerLink: ['/fuel-management/marketplace'],
                                    visible: isFuelManagementSubscribed && can.canViewFuelMarketplace
                                }
                            ]
                        },
                        {
                            label: 'Warehousing Setup',
                            icon: 'fa-solid fa-sliders',
                            visible: isWarehousingSubscribed && can.canViewWarehousingSetupMenu,
                            items: [
                                {
                                    label: 'Storage Rates',
                                    icon: 'fa-solid fa-money-bill-wave',
                                    routerLink: ['/warehousing/setup/storage-rates'],
                                    visible: can.canManageWarehousingStorageRates
                                },
                                {
                                    label: 'Hub Lanes',
                                    icon: 'fa-solid fa-network-wired',
                                    routerLink: ['/warehousing/setup/hub-lanes'],
                                    visible: can.canViewWarehousingSetup
                                },
                                {
                                    label: 'Zones',
                                    icon: 'fa-solid fa-border-all',
                                    routerLink: ['/warehousing/setup/zones'],
                                    visible: can.canViewWarehousingSetup
                                },
                                {
                                    label: 'Item Conditions',
                                    icon: 'fa-solid fa-circle-check',
                                    routerLink: ['/warehousing/setup/item-conditions'],
                                    visible: can.canViewWarehousingSetup
                                },
                                {
                                    label: 'Tenant UOMs',
                                    icon: 'fa-solid fa-ruler-combined',
                                    routerLink: ['/warehousing/setup/uoms'],
                                    visible: can.canViewWarehousingSetup
                                },
                                {
                                    label: 'Categories',
                                    icon: 'fa-solid fa-folder',
                                    routerLink: ['/warehousing/setup/categories'],
                                    visible: can.canViewWarehousingSetup
                                },
                                {
                                    label: 'Brands',
                                    icon: 'fa-solid fa-star',
                                    routerLink: ['/warehousing/setup/brands'],
                                    visible: can.canViewWarehousingSetup
                                },
                                {
                                    label: 'Sub Brands',
                                    icon: 'fa-solid fa-certificate',
                                    routerLink: ['/warehousing/setup/sub-brands'],
                                    visible: can.canViewWarehousingSetup
                                }
                            ]
                        }
                    ]
                }
            ])
        };
    }

    private buildProfileMenu(can: AppMenuPermissions, onLogout: () => void): MenuItem[] {
        const languageItems: MenuItem[] = this.localeService.locales.map(locale => ({
            label: locale.nativeName,
            icon: this.localeService.locale() === locale.code ? 'pi pi-check' : undefined,
            command: () => this.changeLocale(locale.code)
        }));

        return [
            {
                label: this.localeService.translate('profileMenu.profile'),
                icon: 'pi pi-user',
                routerLink: ['/customer'],
                visible: can.canViewProfile
            },
            {
                label: this.localeService.translate('profileMenu.signOut'),
                icon: 'pi pi-sign-out',
                command: () => onLogout()
            },
            {
                separator: true
            },
            {
                label: this.localeService.translate('profileMenu.language'),
                icon: 'pi pi-language',
                disabled: true
            },
            ...languageItems
        ];
    }

    private localizeNavigation(items: MenuItem[]): MenuItem[] {
        return items.map(item => {
            const label = item.label?.trim();
            const key = label ? `navigation.${this.navigationKey(label)}` : '';
            const translated = key && label ? this.localeService.translateIfPresent(key, label) : label;

            return {
                ...item,
                label: translated,
                items: item.items ? this.localizeNavigation(item.items) : item.items
            };
        });
    }

    private navigationKey(label: string): string {
        return label
            .toLowerCase()
            .replace(/&/g, ' and ')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_|_$/g, '');
    }

    private changeLocale(locale: SupportedLocale): void {
        void this.localeService.use(locale);
    }
}
